import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getNotificationsMeta } from "./pushNotificationsApi";

import FormModal from "../../../components/form/FormModal";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";
import FormTextarea from "../../../components/form/FormTextarea";

import "./pushNotifications.css";

const emptyForm = {
  title: "",
  message: "",
  type: "",
  target_type: "all",
  target_value: "",
  redirect_type: "",
  redirect_value: "",
  booking_id: "",
  dataJson: "",
};

const NotificationForm = ({
  open,
  onCancel,
  onSubmit,
  loading,
}) => {
  const [metaLoading, setMetaLoading] = useState(true);

  const [meta, setMeta] = useState({
    targetTypes: [],
    notificationTypes: [],
    roles: [],
    users: [],
    countries: [],
  });

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    setForm(emptyForm);
  }, [open]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        setMetaLoading(true);

        const data = await getNotificationsMeta();

        setMeta({
          targetTypes: Array.isArray(data?.targetTypes) ? data.targetTypes : [],
          notificationTypes: Array.isArray(data?.notificationTypes)
            ? data.notificationTypes
            : [],
          roles: Array.isArray(data?.roles) ? data.roles : [],
          users: Array.isArray(data?.users) ? data.users : [],
          countries: Array.isArray(data?.countries) ? data.countries : [],
        });

        if (
          Array.isArray(data?.notificationTypes) &&
          data.notificationTypes.length > 0
        ) {
          setForm((prev) => ({
            ...prev,
            type: prev.type || data.notificationTypes[0].value,
          }));
        }
      } catch (e) {
        console.error("Failed to load notification meta", e);
        toast.error("Failed to load dropdown values");
      } finally {
        setMetaLoading(false);
      }
    };

    loadMeta();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTargetTypeChange = (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      target_type: value,
      target_value: "",
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!form.title.trim() || !form.message.trim()) {
      toast.error("Title and message are required");
      return;
    }

    if (form.target_type === "single" && !form.target_value) {
      toast.error("Please select a user");
      return;
    }

    if (form.target_type === "country" && !form.target_value) {
      toast.error("Please select a country");
      return;
    }

    if (form.target_type === "role" && !form.target_value) {
      toast.error("Please select a role");
      return;
    }

    let data = {};

    if (form.dataJson.trim()) {
      try {
        data = JSON.parse(form.dataJson);
      } catch {
        toast.error("Optional data must be valid JSON");
        return;
      }
    }

    const payload = {
      title: form.title.trim(),
      message: form.message.trim(),
      target_type: form.target_type,
      target_value: form.target_value || undefined,
      type: form.type || undefined,
      redirect_type: form.redirect_type || undefined,
      redirect_value: form.redirect_value || undefined,
      booking_id: form.booking_id || undefined,
      data,
    };

    onSubmit?.(payload);
  };

  const { targetTypes, notificationTypes, roles, users, countries } = meta;
  const disabled = metaLoading || loading;

  return (
    <FormModal
      open={open}
      title="Compose Notification"
      width="820px"
      submitting={disabled}
      submitText="Send Notification"
      submittingText={loading ? "Sending..." : "Loading..."}
      cancelText="Cancel"
      onClose={onCancel}
      onSubmit={handleSubmit}
    >
      <div className="pn-form">
        <div className="pn-grid">
          <FormField label="Title">
            <FormInput
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              disabled={disabled}
              placeholder="Weekend Offer!"
            />
          </FormField>

          <FormField label="Notification Type">
            <FormSelect
              name="type"
              value={form.type}
              onChange={handleChange}
              disabled={disabled}
            >
              {notificationTypes.length === 0 && (
                <option value="">Loading…</option>
              )}

              {notificationTypes.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="Message" className="pn-span-2">
            <FormTextarea
              name="message"
              rows={3}
              value={form.message}
              onChange={handleChange}
              disabled={disabled}
              placeholder="Book your turf now and get 10% off!"
            />
          </FormField>

          <FormField label="Target">
            <FormSelect
              name="target_type"
              value={form.target_type}
              onChange={handleTargetTypeChange}
              disabled={disabled}
            >
              {targetTypes.length === 0 && (
                <option value="all">All users</option>
              )}

              {targetTypes.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </FormSelect>
          </FormField>

          {form.target_type === "single" && (
            <FormField
              label="User"
              hint="Only users with an active device / FCM token are listed."
            >
              <FormSelect
                value={form.target_value}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    target_value: event.target.value,
                  }))
                }
                disabled={disabled}
              >
                <option value="">Select user…</option>

                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </FormSelect>
            </FormField>
          )}

          {form.target_type === "role" && (
            <FormField
              label="Role"
              hint="Role targeting is advanced. If backend is not wired yet, this will return an error."
            >
              <FormSelect
                value={form.target_value}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    target_value: event.target.value,
                  }))
                }
                disabled={disabled}
              >
                <option value="">Select role…</option>

                {roles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </FormSelect>
            </FormField>
          )}

          {form.target_type === "country" && (
            <FormField
              label="Country"
              hint="Sends notification to all users in the selected country."
            >
              {countries && countries.length > 0 ? (
                <FormSelect
                  value={form.target_value}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      target_value: event.target.value,
                    }))
                  }
                  disabled={disabled}
                >
                  <option value="">Select country…</option>

                  {countries.map((country) => (
                    <option
                      key={country.code || country.value}
                      value={country.code || country.value}
                    >
                      {country.label || country.code}
                    </option>
                  ))}
                </FormSelect>
              ) : (
                <FormInput
                  type="text"
                  value={form.target_value}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      target_value: event.target.value,
                    }))
                  }
                  disabled={disabled}
                  placeholder="e.g. IN, NP, KSA"
                />
              )}
            </FormField>
          )}

          {form.target_type === "all" && (
            <FormField label="Target Value">
              <FormInput
                type="text"
                disabled
                value="All users (no value needed)"
              />
            </FormField>
          )}

          <FormField label="Redirect Type">
            <FormSelect
              name="redirect_type"
              value={form.redirect_type}
              onChange={handleChange}
              disabled={disabled}
            >
              <option value="">None (open Home)</option>
              <option value="listing">Listing</option>
              <option value="venue">Venue</option>
              <option value="external">External URL</option>
            </FormSelect>
          </FormField>

          <FormField label="Redirect Value">
            <FormInput
              type="text"
              name="redirect_value"
              value={form.redirect_value}
              onChange={handleChange}
              disabled={disabled}
              placeholder="e.g. football, green-turf-arena or https://onground.in/offers/diwali"
            />
          </FormField>

          <FormField label="Booking ID (optional)">
            <FormInput
              type="text"
              name="booking_id"
              value={form.booking_id}
              onChange={handleChange}
              disabled={disabled}
              placeholder="ONG-12345"
            />
          </FormField>

          <FormField label="Optional Data (JSON)">
            <FormTextarea
              name="dataJson"
              rows={3}
              value={form.dataJson}
              onChange={handleChange}
              disabled={disabled}
              placeholder={`Example:\n{\n  "extra": "value",\n  "campaign": "Diwali2025"\n}`}
            />
          </FormField>
        </div>
      </div>
    </FormModal>
  );
};

export default NotificationForm;